import {Params, redirect as routerRedirect} from 'react-router-dom';
import client from './client';
import queries from './queries';
import {Utility} from '@/utils';
import RouteParser from '@/parsers/RouterParser';
import MetaParser from '@/parsers/MetaParser';
import consumer from '@/utils/consumer';
import {Strings} from '@/assets';
import {captureMessage, setUser} from '@sentry/react';

type Args = {params: Params<string>; request: Request};

const loaders = {
  meta: async ({params: {country = ''}, request}: Args) => {
    Utility.BASE_URL = country;
    const url = new URL(request.url);
    const pathname = url.pathname;
    Utility.showLoader();
    try {
      const meta = await client.fetchQuery(queries.meta);
      const valid = [`/${country}/`, `/${country}`];
      if (meta.home_page_url && valid.includes(pathname)) {
        return routerRedirect(meta.home_page_url);
      } else {
        return meta;
      }
    } catch (error) {
      return error;
    } finally {
      Utility.hideLoader();
    }
  },
  visa_request: async ({params: {visa_request_id}, request}: Args) => {
    Utility.showLoader();
    const id = visa_request_id!;
    const location = new URL(request.url);
    const route = location.pathname.split('/').pop();
    try {
      const response = await client.fetchQuery(queries.visaRequest(id));
      await client.fetchQuery(queries.visitors(id));
      const meta = new MetaParser(undefined, id);
      if (route) {
        return response;
      } else {
        const route = new RouteParser(request, response, meta);
        setUser(route.user);
        return route.handleRouting();
      }
    } finally {
      Utility.hideLoader();
    }
  },
  redirection: async ({params: {visa_request_id}, request}: Args) => {
    const id = visa_request_id!;
    Utility.showLoader();
    try {
      const response = await client.fetchQuery(queries.visaRequest(id));
      await client.fetchQuery(queries.visitors(id));
      const meta = new MetaParser(undefined, id);
      const route = new RouteParser(request, response, meta);
      return route.redirection();
    } finally {
      Utility.hideLoader();
    }
  },
  visa_application: async ({
    params: {visa_application_token},
    request,
  }: Args) => {
    Utility.showLoader();
    const url = new URL(request.url);
    const visa_request_id = url.searchParams.get('id');
    const error = url.searchParams.get('error');

    const hasId = async (id: string) => {
      const visaRequest = await client.fetchQuery(queries.visaRequest(id));
      await client.fetchQuery(queries.visitors(id));
      const meta = new MetaParser(undefined, id);
      const route = new RouteParser(request, visaRequest, meta);
      Utility.hideLoader();
      return route.handleRouting();
    };

    if (visa_request_id) {
      return await hasId(visa_request_id);
    } else if (error) {
      Utility.hideLoader();
      return error;
    } else {
      try {
        const response = await queries.start(visa_application_token);
        if (response.id) {
          return await hasId(response.id);
        }
      } catch (error) {
        return error;
      }
      const loadRequest = () =>
        new Promise<ApplicationTokenChannel>(resolve => {
          consumer.subscriptions.create(
            {
              channel: 'VerificationsChannel',
              event: 'georgia_info',
              visitor_id: visa_application_token,
            },
            {
              received: async (data: ApplicationTokenChannel) => {
                if (data.success) {
                  const {visa_request_id} = data;
                  await client.fetchQuery(queries.visaRequest(visa_request_id));
                  await client.fetchQuery(queries.visitors(visa_request_id));
                }
                Utility.hideLoader();
                resolve(data);
              },
              connected: () => console.log('connected'),
              disconnected: () => console.log('disconnected'),
            },
          );
        });

      const data = await loadRequest();
      if (data.success) {
        const meta = new MetaParser(undefined, data.visa_request_id);
        const route = new RouteParser(request, data, meta);
        captureMessage(route.capture_message, {
          extra: route.user,
        });
        return route.redirect(Strings.ROUTES.PAYMENT);
      } else {
        return data;
      }
    }
  },
};

export default loaders;
